import React, { useEffect } from 'react'
import classnames from 'classnames/bind'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

// Libs
import { isLiveMallClub } from '@/constants/systemType'

// Components
import Icon from '@/assets/icons'
import ImageListSkeleton from '../../component/ImageList/skeleton/ImageListSkeleton'
import ProductCardSkeleton from '@/components/shared/ProductCard/skeleton/ProductCardSkeleton'
import GoBackNavi from '@/components/shared/GoBackNavi'

// Constants

// Styles
import styles from './style.module.scss'
import 'react-loading-skeleton/dist/skeleton.css'

// Mock Data

const cx = classnames.bind(styles)

const ProductSkeleton = ({ isWebview }: { isWebview?: boolean }) => {
  const { t } = useTranslation()
  const isDesktop = !isWebview
  const isMobile = isWebview
  const router = useRouter()

  const [isDesktopLayout, setIsDesktopLayout] = React.useState(true)
  useEffect(() => {
    if (!router.isReady) {
      return
    }
    if (isDesktop) {
      setIsDesktopLayout(true)
    } else {
      setIsDesktopLayout(false)
    }
  }, [router.isReady, isDesktop])

  return (
    <>
      <SkeletonTheme baseColor='#EAEAEA' highlightColor='#F5F5F5' duration={2}>
        <div className={cx('product')}>
          <div className={cx('container')}>
            {/* 分類麵包屑佔位，與 ValidProduct 的 Breadcrumb 位置、尺寸對齊 */}
            {!isLiveMallClub && <Skeleton width={200} height={18} borderRadius={100} containerClassName={cx('product-breadcrumb')} />}
            {/* 標題留白：回上一頁/回商城 尚未由 useBackRef 判定，先不顯示文字避免閃錯字 */}
            <GoBackNavi title='' />

            <div className={cx('product-info-wrapper')}>
              <div className={cx('product-info')}>
                <Skeleton height={'100%'} width={'100%'} containerClassName={cx('product-info-image')} />

                {!isDesktopLayout && <ImageListSkeleton />}

                <div className={cx('product-info-specs')}>
                  {isDesktopLayout && <Skeleton height={'100%'} borderRadius={100} containerClassName={cx('product-info-specs-name')} />}
                  <Skeleton height={30} width={120} borderRadius={100} containerClassName={cx('product-info-specs-discount')} />

                  {isDesktopLayout && (
                    <React.Fragment>
                      <div className={cx('product-info-specs-content')}>
                        <Skeleton width={70} height={30} borderRadius={100} />
                        <Skeleton width={300} height={30} borderRadius={100} />
                      </div>
                      <div className={cx('product-info-specs-content')}>
                        <Skeleton width={70} height={30} borderRadius={100} />
                        <Skeleton width={300} height={30} borderRadius={100} />
                      </div>
                      <div className={cx('product-info-specs-content')}>
                        <Skeleton width={70} height={30} borderRadius={100} />
                        <Skeleton width={300} height={30} borderRadius={100} />
                      </div>
                    </React.Fragment>
                  )}
                  {!isDesktopLayout && (
                    <>
                      <div className={cx('product-info-drawer')}>
                        <Skeleton width={'100%'} height={20} borderRadius={100} />
                      </div>
                      <div className={cx('product-info-drawer')}>
                        <Skeleton width={'100%'} height={52} borderRadius={10} />
                      </div>
                    </>
                  )}

                  <div className={cx('product-info-specs-tag-wrapper')}>
                    <div className={cx('product-info-specs-tag-wrapper-item')}>
                      <div className={cx('product-info-specs-tag-wrapper-icon')}>
                        <Icon.Protect />
                      </div>
                      <div className={cx('product-info-specs-tag-wrapper-text')}>{t('Product_Guarantee')}</div>
                    </div>
                    <div className={cx('product-info-specs-tag-wrapper-item')}>
                      <div className={cx('product-info-specs-tag-wrapper-icon')}>
                        <Icon.Heart />
                      </div>
                      <div className={cx('product-info-specs-tag-wrapper-text')}>{t('Product_Returns')}</div>
                    </div>
                    <div className={cx('product-info-specs-tag-wrapper-item')}>
                      <div className={cx('product-info-specs-tag-wrapper-icon')}>
                        <Icon.Magnifier />
                      </div>
                      <div className={cx('product-info-specs-tag-wrapper-text')}>{t('Product_SevenDays')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {isDesktopLayout && <ImageListSkeleton />}
            </div>

            <div className={cx('product-merchant')}>
              <div className={cx('product-merchant-info')}>
                <div className={cx('product-merchant-info-image')}>
                  <Skeleton height={'100%'} width={'100%'} containerClassName={cx('product-merchant-info-image-container')} />
                </div>
                <div className={cx('product-merchant-info-wrapper')}>
                  <Skeleton height={20} width={120} borderRadius={100} />
                  <Skeleton height={16} width={80} borderRadius={100} />
                </div>
              </div>
              {isDesktopLayout && <Skeleton height={38} width={108} borderRadius={100} containerClassName={cx('product-merchant-action')} />}
            </div>

            <div className={cx('product-promo')}>
              <div className={cx('product-promo-title')}>{t('Product_Recommend')}</div>
              <div className={cx('product-promo-link')}>{t('Product_More')}</div>
            </div>

            <div className={cx('product__list')}>
              {Array.from(Array(6)).map((_, index) => (
                <ProductCardSkeleton isPromo key={index} className={cx('product__list-card')} />
              ))}
            </div>

            <div className={cx('product-promo')}>
              <div className={cx('product-promo-title')}>{t('Product_Detail')}</div>
            </div>
            <div className={cx('product-detail')}>
              {/* <Skeleton containerClassName={cx('product-detail-content')} /> */}
              <div className={cx('product-detail-content')}>
                <SkeletonTheme borderRadius={100}>
                  {Array.from(Array(12)).map((_, index) => (
                    <div key={index} className={cx('product-detail-item')}>
                      <Skeleton containerClassName={cx('product-detail-item-title')} />
                      <Skeleton containerClassName={cx('product-detail-item-description')} />
                    </div>
                  ))}
                </SkeletonTheme>
              </div>
            </div>
            <div className={cx('product-detail', 'center')}>
              <SkeletonTheme width={isMobile ? 60 : 90} height={isMobile ? 60 : 90}>
                {<Skeleton />}
                {<Skeleton />}
                {<Skeleton />}
              </SkeletonTheme>
            </div>
          </div>
        </div>
      </SkeletonTheme>
    </>
  )
}

export default ProductSkeleton
